import { expValEquals } from '@confluence/feature-experiments';

import type { ContentBlogUnifiedQuery$variables } from './__generated__/ContentBlogUnifiedQuery.graphql.ts';
import type { ContentUnifiedQuery$variables } from './__generated__/ContentUnifiedQuery.graphql.ts';
import type { ContentUnifiedWithSpaceKeyQuery$variables } from './__generated__/ContentUnifiedWithSpaceKeyQuery.graphql.ts';

const DEFAULT_BYLINE_CONTRIBUTORS_FETCH_LIMIT = 4;

type SharedVariableOptions = {
	spaceKey?: string | null;
	versionOverride: number | null;
	isSSR: boolean;
	preloadLabels?: boolean;
	includeAgentTrustReceiptData?: boolean;
	includeBylineContributors?: boolean;
	includeAutomationDiscoveries?: boolean;
	includeAudioContent?: boolean;
	includeRendererAssetsMedia?: boolean;
	includeRendererAssetsCustomChrome?: boolean;
	includeMetadata?: boolean;
	includeClassification?: boolean;
	includeContentHeader?: boolean;
	includeContentHeaderAccess?: boolean;
	includeFabricEditorEligibility?: boolean;
	includeReactions?: boolean;
	includeRestrictions?: boolean;
	includeRestrictionAncestors?: boolean;
	useContentModeFragment?: boolean;
	autohighlightsQueryVariables?: {
		entityAri: string;
		workspaceId: string;
	};
};

type ContentVariableOptions = SharedVariableOptions & {
	contentId: string;
	includeAnalyticsByline?: boolean;
	includeContentHeaderPublicLink?: boolean;
	includeRendererAssetsDsp?: boolean;
	rendererAssetsDspAction?: 'ATTACHMENT_DOWNLOAD' | 'PAGE_EXPORT';
};

type PageContentVariableOptions = ContentVariableOptions & {
	agentSessionsUserId?: string | null;
	includeConnectionAncestors?: boolean;
	// Managed (external) labels are page-scoped, so this option lives on the page
	// query variables only (not the blog or space-key variants). When present, it
	// gates the `useExternalLabels_query` spread on `ContentUnifiedQuery` on and
	// supplies its non-null `cloudId`/`ari` arguments.
	externalLabelsQueryVariables?: {
		cloudId: string;
		ari: string;
	};
};

type SpaceKeyVariableOptions = Omit<SharedVariableOptions, 'spaceKey'> & {
	spaceKey: string;
	agentSessionsUserId?: string | null;
	includeConnectionAncestors?: boolean;
};

const createSharedVariables = ({
	spaceKey,
	versionOverride,
	isSSR,
	preloadLabels = false,
	includeAgentTrustReceiptData = false,
	includeAutomationDiscoveries = false,
	includeAudioContent = false,
	includeRendererAssetsMedia = false,
	includeRendererAssetsCustomChrome = false,
	includeMetadata = false,
	includeClassification = false,
	includeContentHeader = false,
	includeContentHeaderAccess = false,
	includeFabricEditorEligibility = false,
	includeReactions = false,
	includeRestrictions = false,
	includeRestrictionAncestors = false,
	useContentModeFragment = false,
	autohighlightsQueryVariables,
}: SharedVariableOptions) => ({
	spaceKey,
	versionOverride,
	isSSR,
	useNewContentTopper: true,
	...(preloadLabels || includeMetadata ? { preloadLabels: true } : {}),
	...(includeAgentTrustReceiptData ? { includeAgentTrustReceiptData: true } : {}),
	...(includeAutomationDiscoveries ? { includeAutomationDiscoveries: true } : {}),
	...(includeAudioContent ? { includeAudioContent: true } : {}),
	...(includeRendererAssetsMedia ? { includeRendererAssetsMedia: true } : {}),
	...(includeRendererAssetsCustomChrome ? { includeRendererAssetsCustomChrome: true } : {}),
	...(includeClassification || includeMetadata ? { includeClassification: true } : {}),
	...((includeClassification || includeMetadata) &&
	expValEquals('confluence_classification_level_v2', 'isEnabled', true)
		? { useClassificationLevelV2: true }
		: {}),
	...(includeContentHeader ? { includeContentHeader: true } : {}),
	...(includeContentHeaderAccess ? { includeContentHeaderAccess: true } : {}),
	...(includeFabricEditorEligibility ? { includeFabricEditorEligibility: true } : {}),
	...(includeReactions ? { includeReactions: true } : {}),
	...(includeRestrictions ? { includeRestrictions: true } : {}),
	...(includeRestrictionAncestors ? { includeRestrictionAncestors: true } : {}),
	...(useContentModeFragment ? { useContentModeFragment: true } : {}),
	...(autohighlightsQueryVariables
		? {
				includeAutohighlights: true,
				autohighlightsEntityAri: autohighlightsQueryVariables.entityAri,
				autohighlightsWorkspaceId: autohighlightsQueryVariables.workspaceId,
			}
		: {}),
});

const createAgentSessionVariables = (agentSessionsUserId?: string | null) =>
	agentSessionsUserId
		? {
				agentSessionsPropertyKey: `cc-agent-mention-sessions:${agentSessionsUserId}`,
				fetchAgentSessionsMarker: true,
			}
		: {};

const createBylineVariables = (includeBylineContributors = false) =>
	includeBylineContributors
		? {
				includeBylineContributors: true,
				bylineContributorsLimit: DEFAULT_BYLINE_CONTRIBUTORS_FETCH_LIMIT,
			}
		: {};

const createAnalyticsBylineVariables = (includeAnalyticsByline = false) =>
	includeAnalyticsByline ? { includeAnalyticsByline: true } : {};

const createConnectionAncestorVariables = (includeConnectionAncestors = false) =>
	includeConnectionAncestors ? { includeConnectionAncestors: true } : {};

export const createContentUnifiedQueryVariables = ({
	contentId,
	agentSessionsUserId,
	includeBylineContributors,
	includeConnectionAncestors,
	includeAnalyticsByline,
	includeContentHeaderPublicLink,
	includeRendererAssetsDsp,
	rendererAssetsDspAction,
	externalLabelsQueryVariables,
	...sharedOptions
}: PageContentVariableOptions): ContentUnifiedQuery$variables => ({
	contentId,
	...createSharedVariables(sharedOptions),
	...createAgentSessionVariables(agentSessionsUserId),
	...createBylineVariables(includeBylineContributors),
	...createConnectionAncestorVariables(includeConnectionAncestors),
	...createAnalyticsBylineVariables(includeAnalyticsByline),
	...(includeContentHeaderPublicLink ? { includeContentHeaderPublicLink: true } : {}),
	...(includeRendererAssetsDsp
		? {
				includeRendererAssetsDsp: true,
				rendererAssetsDspAction: rendererAssetsDspAction || 'PAGE_EXPORT',
			}
		: {}),
	// Managed (external) labels: emit the include flag and args only when the
	// feature is on for this content. `externalLabelsCloudId`/`externalLabelsAri`
	// are declared nullable-with-default (`ID = ""`) on `ContentUnifiedQuery`, so
	// omitting them here is safe — GraphQL supplies the default, and a
	// nullable-with-default variable still satisfies the fragment's non-null
	// `ID!` argument via the variable-usage rule. Mirrors the autohighlights
	// pattern so omitting consumers get an unchanged variables object.
	...(externalLabelsQueryVariables
		? {
				includeExternalLabels: true,
				externalLabelsCloudId: externalLabelsQueryVariables.cloudId,
				externalLabelsAri: externalLabelsQueryVariables.ari,
			}
		: {}),
});

export const createContentBlogUnifiedQueryVariables = ({
	contentId,
	includeBylineContributors,
	includeAnalyticsByline,
	includeContentHeaderPublicLink,
	includeRendererAssetsDsp,
	rendererAssetsDspAction,
	...sharedOptions
}: ContentVariableOptions): ContentBlogUnifiedQuery$variables => ({
	contentId,
	...createSharedVariables(sharedOptions),
	...createBylineVariables(includeBylineContributors),
	...createAnalyticsBylineVariables(includeAnalyticsByline),
	...(includeContentHeaderPublicLink ? { includeContentHeaderPublicLink: true } : {}),
	...(includeRendererAssetsDsp
		? {
				includeRendererAssetsDsp: true,
				rendererAssetsDspAction: rendererAssetsDspAction || 'PAGE_EXPORT',
			}
		: {}),
});

export const createContentUnifiedWithSpaceKeyQueryVariables = ({
	spaceKey,
	agentSessionsUserId,
	includeBylineContributors,
	includeConnectionAncestors,
	...sharedOptions
}: SpaceKeyVariableOptions): ContentUnifiedWithSpaceKeyQuery$variables => ({
	...createSharedVariables({ ...sharedOptions, spaceKey }),
	spaceKey,
	...createAgentSessionVariables(agentSessionsUserId),
	...(includeBylineContributors ? { includeBylineContributors: true } : {}),
	...createConnectionAncestorVariables(includeConnectionAncestors),
});
